/**
 * PayslipsBulkImport — Wizard 3 étapes pour import massif bulletins.
 *
 *  Step 1 : drag-drop ZIP ou multi-PDF + choix période
 *  Step 2 : tableau des propositions matchées (confidence + strategy)
 *           + dropdown de correction manuelle + gestion doublons
 *  Step 3 : rapport final (created / replaced / skipped / cascade payroll)
 */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadCloud, FileArchive, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, XCircle, Loader2, Sparkles, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";

const MONTH_LABELS = ["janv.","févr.","mars","avr.","mai","juin","juil.","août","sept.","oct.","nov.","déc."];
const formatPeriod = (p) => {
  if (!p || p.length < 7) return p;
  const [y, m] = p.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1] || m} ${y}`;
};
const kb = (n) => n < 1024 ? `${n} o` : n < 1024 * 1024 ? `${(n/1024).toFixed(0)} Ko` : `${(n/1024/1024).toFixed(1)} Mo`;

function confidenceColor(c) {
  if (c >= 95) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (c >= 88) return "bg-blue-100 text-blue-700 border-blue-300";
  if (c >= 70) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-red-100 text-red-700 border-red-300";
}

const STRATEGY_LABEL = {
  exact_full_name: "Nom + prénom exact",
  fuzzy_full_name: "Fuzzy nom+prénom",
  last_name_only: "Nom seul (à vérifier)",
  ai_header: "IA entête PDF",
};

export default function PayslipsBulkImport() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [period, setPeriod] = useState("");  // Vide = auto-détection IA par fichier
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [assignments, setAssignments] = useState({});  // filename → {employee_id, period, replace_duplicate, action}
  const [cascadePayroll, setCascadePayroll] = useState(true);
  const [report, setReport] = useState(null);

  // ------ Step 1 handlers ------

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    setFiles(prev => [...prev, ...dropped]);
  };

  const totalSize = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);

  const runPreview = async () => {
    if (!files.length) { toast.error("Ajoute au moins un fichier"); return; }
    setLoading(true);
    const fd = new FormData();
    if (period) fd.append("period", period);
    for (const f of files) fd.append("files", f);
    try {
      const r = await api.post("/hr/payslips/bulk/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      // Pre-fill assignments with suggested + period détectée
      const init = {};
      for (const p of r.data.proposals) {
        init[p.filename] = {
          employee_id: p.suggested_employee_id,
          period: p.detected_period || "",
          replace_duplicate: false,
          action: "create",
        };
      }
      setAssignments(init);
      setStep(2);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  // ------ Step 2 handlers ------

  const updateAssign = (filename, patch) => {
    setAssignments(prev => ({ ...prev, [filename]: { ...prev[filename], ...patch } }));
  };

  const runConfirm = async () => {
    if (!preview) return;
    const items = preview.proposals
      .filter(p => assignments[p.filename] && assignments[p.filename].action === "create")
      .map(p => ({
        filename: p.filename,
        employee_id: assignments[p.filename].employee_id,
        period: assignments[p.filename].period || null,
        replace_duplicate: !!assignments[p.filename].replace_duplicate,
        action: "create",
      }));
    if (!items.length) { toast.error("Sélectionne au moins un bulletin à créer"); return; }
    // Guard côté client : chaque bulletin doit avoir une période résoluble
    const missing = items.filter(i => !i.period && !period);
    if (missing.length) {
      toast.error(`${missing.length} bulletin(s) sans période détectée — corrige la colonne "Période"`);
      return;
    }

    setLoading(true);
    try {
      const r = await api.post("/hr/payslips/bulk/confirm", {
        stage_id: preview.stage_id,
        period: period || null,
        assignments: items,
        cascade_payroll_variables: cascadePayroll,
      });
      setReport(r.data);
      setStep(3);
      toast.success(`${r.data.summary.created_count} bulletin(s) créé(s)`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const proposalStats = useMemo(() => {
    if (!preview) return null;
    const hi = preview.proposals.filter(p => p.confidence >= 95).length;
    const mid = preview.proposals.filter(p => p.confidence >= 88 && p.confidence < 95).length;
    const low = preview.proposals.filter(p => p.confidence < 88).length;
    const dup = preview.proposals.filter(p => p.is_duplicate).length;
    return { hi, mid, low, dup };
  }, [preview]);

  return (
    <div className="space-y-6" data-testid="payslips-bulk-import-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Import massif bulletins</h1>
          <p className="text-sm text-slate-500">
            ZIP ou multi-PDF · Matching automatique nom + prénom · Rollback via l'écran Bulletins
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/app/hr/payslips")}>
          <ArrowLeft className="w-4 h-4 mr-2" />Retour Bulletins
        </Button>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        <StepPill n={1} label="Sélection" active={step === 1} done={step > 1} />
        <div className="h-px flex-1 bg-slate-200" />
        <StepPill n={2} label="Rapprochement" active={step === 2} done={step > 2} />
        <div className="h-px flex-1 bg-slate-200" />
        <StepPill n={3} label="Rapport" active={step === 3} done={false} />
      </div>

      {/* ===== STEP 1 ===== */}
      {step === 1 && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <Label htmlFor="period">Période (mois) <span className="text-xs text-slate-400 font-normal">— optionnel</span></Label>
                <Input id="period" type="month" value={period}
                  onChange={e => setPeriod(e.target.value)}
                  data-testid="period-input"
                  placeholder="Auto-détectée par l'IA" />
                <p className="text-xs text-slate-500 mt-1">
                  <Sparkles className="w-3 h-3 inline text-purple-500" />
                  Laisser vide : l'IA lit la période sur chaque bulletin
                </p>
              </div>
              <div className="md:col-span-2">
                <Label>Fichiers</Label>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  className={`mt-1 border-2 border-dashed rounded-lg p-8 text-center transition ${
                    dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50"
                  }`}
                  data-testid="drop-zone"
                >
                  <FileArchive className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm font-medium">Glissez un ZIP ou plusieurs PDF ici</p>
                  <p className="text-xs text-slate-500 mt-1">ou</p>
                  <label className="inline-block mt-2">
                    <input id="payslips-file-input" name="payslips-file-input" type="file" multiple accept=".pdf,.zip,application/pdf,application/zip"
                      className="hidden"
                      onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files || [])])}
                      data-testid="file-input" />
                    <span className="px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm cursor-pointer hover:bg-slate-100 inline-flex items-center gap-1">
                      <UploadCloud className="w-4 h-4" />Parcourir
                    </span>
                  </label>
                  <p className="text-xs text-slate-500 mt-2">
                    Max 100 fichiers/ZIP · PDF ≤ 8 Mo · Nommez les fichiers avec NOM + PRÉNOM
                  </p>
                </div>
              </div>
            </div>

            {files.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{files.length} fichier(s) — {kb(totalSize)}</p>
                  <Button variant="ghost" size="sm" onClick={() => setFiles([])} data-testid="clear-files-btn">Vider</Button>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto text-xs">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-600">
                      <FileText className="w-3 h-3 shrink-0" />
                      <span className="truncate">{f.name}</span>
                      <span className="ml-auto text-slate-400 shrink-0">{kb(f.size)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={runPreview} disabled={loading || !files.length} data-testid="preview-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Analyser & prévisualiser
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== STEP 2 ===== */}
      {step === 2 && preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Total fichiers" value={preview.total_files} />
            <Kpi label="Confidence 95%+" value={proposalStats.hi} tone="emerald" />
            <Kpi label="Confidence 88-94%" value={proposalStats.mid} tone="blue" />
            <Kpi label="Confidence &lt; 88%" value={proposalStats.low} tone="amber" />
            <Kpi label="Doublons période" value={proposalStats.dup} tone={proposalStats.dup ? "amber" : ""} />
          </div>

          <Card>
            <CardContent className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fichier</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Salarié suggéré</TableHead>
                    <TableHead>Correction</TableHead>
                    <TableHead>Période détectée</TableHead>
                    <TableHead>Doublon</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.proposals.map(p => {
                    const a = assignments[p.filename] || {};
                    return (
                      <TableRow key={p.filename} data-testid={`prop-row-${p.filename}`}>
                        <TableCell className="text-xs font-mono max-w-xs truncate" title={p.filename}>{p.filename}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={confidenceColor(p.confidence)} data-testid={`confidence-${p.filename}`}>
                            {p.confidence}% · {STRATEGY_LABEL[p.strategy] || p.strategy}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{p.suggested_employee_name}</div>
                          <div className="text-[10px] text-slate-500">{p.reason}</div>
                        </TableCell>
                        <TableCell>
                          <Select value={a.employee_id || ""}
                            onValueChange={v => updateAssign(p.filename, { employee_id: v })}>
                            <SelectTrigger className="w-56 h-8 text-xs" data-testid={`emp-select-${p.filename}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {preview.employees.map(e => (
                                <SelectItem key={e.id} value={e.id} className="text-xs">{e.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Input type="month" value={a.period || ""}
                              onChange={e => updateAssign(p.filename, { period: e.target.value })}
                              className="h-8 w-32 text-xs font-mono"
                              data-testid={`period-input-${p.filename}`}
                              placeholder="YYYY-MM" />
                            {p.detected_period && p.period_source === "ai" && (
                              <span className="text-[10px] text-purple-600 flex items-center gap-1">
                                <Sparkles className="w-2.5 h-2.5" />IA · {formatPeriod(p.detected_period)}
                              </span>
                            )}
                            {!p.detected_period && (
                              <span className="text-[10px] text-red-600">⚠ Non détectée</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {p.is_duplicate ? (
                            <label className="flex items-center gap-1 text-xs">
                              <Checkbox checked={!!a.replace_duplicate}
                                onCheckedChange={v => updateAssign(p.filename, { replace_duplicate: !!v })}
                                data-testid={`replace-${p.filename}`} />
                              Remplacer
                            </label>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </TableCell>
                        <TableCell>
                          <Select value={a.action || "create"}
                            onValueChange={v => updateAssign(p.filename, { action: v })}>
                            <SelectTrigger className="w-24 h-8 text-xs" data-testid={`action-${p.filename}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="create" className="text-xs">Créer</SelectItem>
                              <SelectItem value="skip" className="text-xs">Ignorer</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {preview.unmatched?.length > 0 && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800 flex items-center gap-1">
                    <XCircle className="w-4 h-4" />{preview.unmatched.length} fichier(s) non matché(s)
                  </p>
                  <ul className="text-xs text-red-700 mt-1 space-y-0.5">
                    {preview.unmatched.map((u, i) => (
                      <li key={i}>• {u.filename} — {u.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              {preview.errors?.length > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />{preview.errors.length} erreur(s)
                  </p>
                  <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                    {preview.errors.map((e, i) => <li key={i}>• {e.filename} — {e.error}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={cascadePayroll} onCheckedChange={v => setCascadePayroll(!!v)}
                  data-testid="cascade-payroll-toggle" />
                <span className="flex items-center gap-1">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  Injecter automatiquement dans les variables de paie du mois
                </span>
              </label>
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
                <Button onClick={runConfirm} disabled={loading} data-testid="confirm-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Confirmer l'import
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== STEP 3 ===== */}
      {step === 3 && report && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                <div>
                  <p className="text-lg font-semibold">Import terminé</p>
                  <p className="text-sm text-slate-500">
                    {report.summary.created_count} créé(s) · {report.summary.replaced_count} remplacé(s) · {report.summary.skipped_count} ignoré(s) · {report.summary.cascade_count} variables de paie injectées
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Créés" value={report.summary.created_count} tone="emerald" />
                <Kpi label="Remplacés" value={report.summary.replaced_count} tone={report.summary.replaced_count ? "blue" : ""} />
                <Kpi label="Ignorés" value={report.summary.skipped_count} />
                <Kpi label="Erreurs" value={report.summary.errors_count} tone={report.summary.errors_count ? "red" : ""} />
                <Kpi label="Cascade paie" value={report.summary.cascade_count} tone="emerald" />
              </div>

              {report.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800">Erreurs</p>
                  <ul className="text-xs text-red-700 mt-1 space-y-0.5">
                    {report.errors.map((e, i) => <li key={i}>• {e.filename} — {e.error}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setStep(1); setFiles([]); setPreview(null); setReport(null); }} data-testid="reset-btn">
              <RefreshCw className="w-4 h-4 mr-1" />Nouvel import
            </Button>
            <Button onClick={() => navigate("/app/hr/payslips")} data-testid="go-payslips-btn">
              Voir les bulletins<ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function StepPill({ n, label, active, done }) {
  const cls = active
    ? "bg-blue-600 text-white border-blue-600"
    : done
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : "bg-white text-slate-500 border-slate-200";
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${cls}`} data-testid={`step-${n}`}>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${done ? "bg-emerald-500 text-white" : active ? "bg-white text-blue-600" : "bg-slate-100 text-slate-500"}`}>
        {done ? "✓" : n}
      </span>
      {label}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const map = {
    emerald: "border-emerald-200 bg-emerald-50",
    blue: "border-blue-200 bg-blue-50",
    amber: "border-amber-200 bg-amber-50",
    red: "border-red-200 bg-red-50",
  };
  const cls = map[tone] || "border-slate-200 bg-white";
  return (
    <div className={`${cls} rounded-lg border p-3`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
